import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { AttendanceStatus } from '../api/admin';
import {
  ATTENDANCE_PERIODS,
  type AttendancePeriod,
  type AttendanceUploadResult,
  dayLabel,
  duration,
  isStale,
  shortTime,
  uploadAttendance,
  uploadAttendanceMapping,
  useAttendanceDashboard,
  useLinkAttendancePerson,
} from '../api/attendance';
import type { Person } from '../api/org';
import { useCurrentUser } from '../api/session';
import { AttendanceDayDetail } from '../components/AttendanceDayDetail';
import { Avatar } from '../components/Avatar';
import { EmployeePicker } from '../components/EmployeePicker';
import { Icon } from '../components/Icon';
import { KpiCard } from '../components/Stats';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle } from '../layout/shellContext';
import { AttendancePeriodPills } from './cabinet/AttendanceSection';

const STATUS = {
  present: { label: 'На месте', className: 'status-ok' },
  unpaired: { label: 'Пропущена отметка', className: 'status-info' },
  absent: { label: 'Нет отметок', className: 'status-neutral' },
} as const;

const moscow = (value: string) => new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' });

/** Качество данных для администратора: откуда пришли отчёты, ручная загрузка, несвязанные люди. */
function DataPanel({ admin }: { admin: AttendanceStatus }) {
  const inputId = useId();
  const [sourceId, setSourceId] = useState<string | null>(admin.sources[0]?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AttendanceUploadResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [linking, setLinking] = useState<Record<string, Person | null>>({});
  const mappingId = useId();
  const [mapping, setMapping] = useState<string | null>(null);
  const link = useLinkAttendancePerson();
  const queryClient = useQueryClient();
  const last = admin.reports[0];
  const sourceName = (id: string) => admin.sources.find((source) => source.id === id)?.name ?? '';

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await uploadAttendance(file, admin.sources.length > 1 ? sourceId : null));
      await queryClient.invalidateQueries({ queryKey: ['attendance'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
      link.reset();
    }
  };

  const uploadMapping = async (file: File) => {
    setBusy(true);
    setError(null);
    setMapping(null);
    try {
      const response = await uploadAttendanceMapping(file, admin.sources.length > 1 ? sourceId : null);
      setMapping(
        `Связано по таблице: ${response.linked}` +
          (response.errorCount
            ? `. Строк с ошибками: ${response.errorCount} — ${response.errors.map((e) => `строка ${e.row}: ${e.message}`).join('; ')}`
            : ''),
      );
      await queryClient.invalidateQueries({ queryKey: ['attendance'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Данные системы проходов</h2>
        {last && isStale(last.receivedAt) ? (
          <span className="status status-bad">Нет новых отчётов больше суток</span>
        ) : null}
        {last ? (
          <span className="reg-version">
            Последний отчёт {moscow(last.receivedAt)} ·{' '}
            {last.uploadedBy ? `загрузил ${last.uploadedBy}` : 'коннектор'}
          </span>
        ) : (
          <span className="status status-neutral">Отчётов ещё не было</span>
        )}
      </div>

      <div className="import-file">
        {admin.sources.length > 1 ? (
          <select
            className="select"
            aria-label="Система проходов"
            value={sourceId ?? ''}
            onChange={(event) => setSourceId(event.target.value)}
          >
            {admin.sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        ) : null}
        <label htmlFor={inputId} className="btn-secondary">
          <Icon name="upload" size={14} />
          {busy ? 'Загружаю…' : 'Загрузить отчёт вручную'}
        </label>
        <input
          id={inputId}
          className="visually-hidden"
          type="file"
          accept=".txt,.csv"
          aria-label="Отчёт о посещениях"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }}
        />
        <label htmlFor={mappingId} className="btn-secondary">
          <Icon name="users" size={14} />
          Таблица сопоставлений
        </label>
        <input
          id={mappingId}
          className="visually-hidden"
          type="file"
          accept=".xlsx,.csv"
          aria-label="Таблица сопоставлений"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void uploadMapping(file);
          }}
        />
        <span className="muted">
          Тот же отчёт повторно не задваивается, новый отчёт за день только дополняет.
        </span>
      </div>
      {mapping ? (
        <p className="notice notice-ok">
          <Icon name="check" size={14} />
          {mapping}
        </p>
      ) : null}
      {error ? <ErrorState title="Отчёт не загружен" error={error} /> : null}
      {result ? (
        <p className={`notice${result.duplicate ? '' : ' notice-ok'}`}>
          <Icon name={result.duplicate ? 'alert' : 'check'} size={14} />
          {result.duplicate
            ? 'Этот отчёт уже был загружен — данные не изменились.'
            : `Принято: новых дней ${result.inserted}, дополнено ${result.updated}, без изменений ${result.unchanged}` +
              `${result.errorCount ? `, строк с ошибками ${result.errorCount}` : ''}.`}
        </p>
      ) : null}

      {admin.unmatched.length ? (
        <>
          <h3 className="section-title attendance-subtitle">
            Не связаны с сотрудниками · {admin.unmatched.length}
          </h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>В системе проходов</th>
                  <th>Последняя отметка</th>
                  <th>Сотрудник</th>
                </tr>
              </thead>
              <tbody>
                {admin.unmatched.map((person) => {
                  const key = `${person.sourceId}:${person.deviceId}`;
                  const chosen = linking[key] ?? null;
                  return (
                    <tr key={key}>
                      <td>
                        <strong>{person.deviceName}</strong>
                        <small>
                          ID {person.deviceId}
                          {admin.sources.length > 1 ? ` · ${sourceName(person.sourceId)}` : ''}
                        </small>
                      </td>
                      <td>{person.lastSeen ? dayLabel(person.lastSeen) : '—'}</td>
                      <td className="attendance-link-cell">
                        <EmployeePicker
                          label="Сотрудник"
                          value={chosen}
                          onChange={(value) => setLinking((current) => ({ ...current, [key]: value }))}
                        />
                        {chosen ? (
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={link.isPending}
                            onClick={() =>
                              link.mutate({
                                sourceId: person.sourceId,
                                deviceId: person.deviceId,
                                employeeId: chosen.id,
                              })
                            }
                          >
                            Связать
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {link.isError ? <ErrorState title="Не связано" error={link.error} /> : null}
        </>
      ) : null}
    </section>
  );
}

/** Посещения по команде: руководитель — свои сотрудники, администратор — вся компания. */
export function AttendancePage() {
  usePageTitle('Посещения');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const [dayOf, setDayOf] = useState<string | null>(null);
  const daysParam = Number(params.get('days'));
  const days = (ATTENDANCE_PERIODS.find((p) => p.days === daysParam)?.days ?? 30) as AttendancePeriod;
  const dashboard = useAttendanceDashboard({
    date: params.get('date'),
    days,
    departmentId: params.get('department'),
  });

  const update = (patch: Record<string, string | null>) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });

  if (dashboard.isPending) return <LoadingState label="Считаю посещения" />;
  if (dashboard.isError)
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  const data = dashboard.data;
  if (data.mode === 'self') return <Navigate to="/me#attendance" replace />;

  const openCabinet = (id: string) =>
    navigate(id === me.data?.employeeId ? '/me#attendance' : `/employees/${id}#attendance`);

  return (
    <div className="page">
      {data.mode === 'none' || !data.totals || !data.department ? (
        <section className="panel">
          <EmptyState title="Посещения сотрудников вам не видны">
            Здесь руководители видят своих сотрудников, администратор — всю компанию.
          </EmptyState>
        </section>
      ) : (
        <>
          <section className="panel period-bar">
            <div className="filters">
              {data.scopes && data.scopes.length > 1 ? (
                <select
                  className="select"
                  aria-label="Подразделение"
                  value={data.department.id}
                  onChange={(event) => update({ department: event.target.value })}
                >
                  {data.scopes.map((scope) => (
                    <option key={scope.id} value={scope.id}>
                      {'· '.repeat(scope.depth)}
                      {scope.name} ({scope.people})
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                className="input"
                type="date"
                aria-label="День"
                value={data.date ?? ''}
                max={data.latestDate ?? undefined}
                onChange={(event) => update({ date: event.target.value || null })}
              />
            </div>
            <AttendancePeriodPills value={days} onChange={(value) => update({ days: String(value) })} />
          </section>

          {data.department.path.length > 1 ? (
            <nav className="org-path" aria-label="Путь">
              {data.department.path.map((step, index) => (
                <span key={step.id} style={{ display: 'contents' }}>
                  {index ? (
                    <span className="sep">
                      <Icon name="chevronRight" size={12} />
                    </span>
                  ) : null}
                  <button
                    type="button"
                    aria-current={step.id === data.department?.id ? 'page' : undefined}
                    onClick={() => update({ department: step.id })}
                  >
                    {step.name}
                  </button>
                </span>
              ))}
            </nav>
          ) : null}

          <div className="kpi-grid five">
            <KpiCard
              label={`На месте ${data.date ? dayLabel(data.date) : ''}`}
              icon="users"
              value={data.totals.present}
              extra={`из ${data.totals.tracked}${data.totals.unpaired ? ` · без ухода ${data.totals.unpaired}` : ''}`}
              tone="good"
            />
            {data.totals.late != null ? (
              <KpiCard
                label="Опоздали"
                icon="clock"
                value={data.totals.late}
                tone={data.totals.late ? 'bad' : 'good'}
              />
            ) : (
              <KpiCard label="Пропущена отметка" icon="clock" value={data.totals.unpaired} />
            )}
            <KpiCard
              label="Нет отметок"
              icon="alert"
              value={data.totals.absent}
              tone={data.totals.absent && !data.weekend ? 'bad' : undefined}
            />
            <KpiCard label="Средний приход" icon="clock" value={data.totals.avgFirst ?? '—'} />
            <KpiCard
              label="На месте в среднем"
              icon="activity"
              value={duration(data.totals.periodAvgMinutes)}
            />
          </div>
          {data.weekend ? (
            <p className="notice">
              <Icon name="clock" size={14} />
              Выходной день: отсутствие не считается, опоздания не учитываются.
            </p>
          ) : null}
          {data.open ? (
            <p className="notice">
              <Icon name="clock" size={14} />
              День ещё идёт: время ухода и на месте станут итоговыми после следующего отчёта.
            </p>
          ) : null}
          {data.totals.untracked ? (
            <p className="muted">
              Не связаны с системой проходов и не учитываются: {data.totals.untracked} сотр.
            </p>
          ) : null}

          {data.trend?.length ? (
            <section className="panel">
              <div className="panel-header">
                <h2>На месте по дням</h2>
              </div>
              <div
                className="attendance-trend"
                role="img"
                aria-label="Сколько сотрудников было на месте по дням"
              >
                {data.trend.map((point) => (
                  <button
                    key={point.date}
                    type="button"
                    className={`trend-bar${point.date === data.date ? ' current' : ''}${point.weekend ? ' weekend' : ''}`}
                    title={`${dayLabel(point.date)}: ${point.present} из ${data.totals?.tracked ?? 0}`}
                    onClick={() => update({ date: point.date })}
                  >
                    <span
                      className="trend-fill"
                      style={{
                        height: `${data.totals?.tracked ? (point.present / data.totals.tracked) * 100 : 0}%`,
                      }}
                    />
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {data.units?.length ? (
            <section className="panel">
              <div className="panel-header">
                <h2>Подразделения</h2>
              </div>
              <div className="table-scroll">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Подразделение</th>
                      <th className="number">В офисе</th>
                      <th className="number">Опоздали</th>
                      <th className="number">Нет отметок</th>
                      <th className="number">Средний приход</th>
                      <th className="number">На месте в среднем</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.units.map((unit) => (
                      <tr key={unit.id} className="clickable" onClick={() => update({ department: unit.id })}>
                        <td>
                          <button
                            type="button"
                            className="row-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              update({ department: unit.id });
                            }}
                          >
                            <strong>{unit.name}</strong>
                          </button>
                        </td>
                        <td className="number">
                          {unit.present} из {unit.tracked}
                        </td>
                        <td className={`number${unit.late ? ' bad' : ''}`}>{unit.late ?? '—'}</td>
                        <td className={`number${unit.absent ? ' bad' : ''}`}>{unit.absent}</td>
                        <td className="number">{unit.avgFirst ?? '—'}</td>
                        <td className="number">{duration(unit.periodAvgMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {data.people?.length ? (
            <section className="panel">
              <div className="panel-header">
                <h2>Сотрудники · {data.department.name}</h2>
              </div>
              <div className="table-scroll">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Сотрудник</th>
                      <th>{data.date ? dayLabel(data.date) : 'День'}</th>
                      <th className="number">Приход</th>
                      <th className="number">Уход</th>
                      <th className="number">На месте</th>
                      <th className="number">Дней за период</th>
                      <th className="number">Опозданий</th>
                      <th className="number">Средний приход</th>
                      <th className="number">В офисе в среднем</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.people.map((person) => (
                      <tr key={person.id} className="clickable" onClick={() => openCabinet(person.id)}>
                        <td>
                          <button
                            type="button"
                            className="person-line row-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              openCabinet(person.id);
                            }}
                          >
                            <Avatar name={person.fullName} photoUrl={person.photoUrl} />
                            <span className="person-line-text">
                              <b>{person.fullName}</b>
                              {person.position ? <small>{person.position}</small> : null}
                            </span>
                          </button>
                        </td>
                        <td>
                          {person.today ? (
                            <button
                              type="button"
                              className="day-open"
                              title="Подробно за день"
                              onClick={(event) => {
                                event.stopPropagation();
                                setDayOf(person.id);
                              }}
                            >
                              <span
                                className={`status ${person.today.lateMinutes != null ? 'status-bad' : STATUS[person.status].className}`}
                              >
                                {person.today.lateMinutes != null
                                  ? `Опоздание ${duration(person.today.lateMinutes)}`
                                  : STATUS[person.status].label}
                              </span>
                              {person.today.breaks.length ? (
                                <small className="day-note">
                                  выходов {person.today.breaks.length} ·{' '}
                                  {duration(person.today.outsideMinutes)}
                                </small>
                              ) : null}
                              <Icon name="chevronRight" size={12} />
                            </button>
                          ) : (
                            <span
                              className={`status ${person.status === 'absent' && data.weekend ? 'status-neutral' : STATUS[person.status].className}`}
                            >
                              {person.status === 'absent' && data.weekend
                                ? 'Выходной'
                                : STATUS[person.status].label}
                            </span>
                          )}
                        </td>
                        <td className="number">{shortTime(person.today?.firstAt ?? null)}</td>
                        <td className="number">
                          {person.today?.open ? (
                            <span className="muted">пока {shortTime(person.today.latestAt)}</span>
                          ) : (
                            shortTime(person.today?.lastAt ?? null)
                          )}
                        </td>
                        <td className="number">{duration(person.today?.minutes ?? null)}</td>
                        <td className="number">
                          {person.period.days}
                          {person.period.unpairedDays ? (
                            <small className="muted"> · {person.period.unpairedDays} с пропуском</small>
                          ) : null}
                        </td>
                        <td className={`number${person.period.lateDays ? ' bad' : ''}`}>
                          {person.period.lateDays ?? '—'}
                        </td>
                        <td className="number">{person.period.avgFirst ?? '—'}</td>
                        <td className="number">{duration(person.period.avgMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      )}

      {data.admin ? <DataPanel admin={data.admin} /> : null}
      {dayOf
        ? (() => {
            const person = data.people?.find((item) => item.id === dayOf);
            return person?.today ? (
              <AttendanceDayDetail
                day={person.today}
                schedule={person.schedule}
                person={person.fullName}
                onClose={() => setDayOf(null)}
              />
            ) : null;
          })()
        : null}
    </div>
  );
}
